/**
 * RENDERING SYSTEM
 *
 * Manages the complete PixiJS rendering pipeline:
 * - Background layers: sea, map, terrain (a sea quad, and a land quad under the polygon stencil mask)
 * - Camera: pan, boundary clamping, screen shake
 * - HUD: energy/health bar render textures, minimap positioning
 * - Terrain, shade and HUD sprites placed into the layers (the layers and their draw order live in Layers.ts)
 *
 * Scene render passes (each frame):
 * 1. HUD containers → their RenderTextures
 * 2. Game container → screen, directly. There is no post-processing pass.
 */

import { GAME_TYPES } from '@airmash/protocol';
import type { AutoDetectOptions, Container, Renderer } from 'pixi.js';
import { Assets, autoDetectRenderer, GlProgram, Graphics as PixiGraphics, RenderTexture, Sprite } from 'pixi.js';
import { uiState } from '../ui/UIState';
import { clock } from './Clock';
import { DISPLAY_DEFAULTS, GAME_DEFAULTS } from './Config';
import { DEVELOPMENT, MAP_CENTER_X, MAP_CENTER_Y, MAP_HEIGHT, MAP_WIDTH, OVERDRAW, SHADOW_SCALING } from './Constants';
import { device } from './Device';
import { mountainFactor as doodadsMountainFactor, setup as doodadsSetup, update as doodadsUpdate } from './Doodads';
import { layers } from './Layers';
import { resizeHorizon as networkResizeHorizon } from './Network';
import { forEach as playersForEach } from './Players';
import { GAME_STATE_CODES, session } from './Session';
import { settings } from './Settings';
import {
  resize as terrainResize,
  setCamera as terrainSetCamera,
  setScale as terrainSetScale,
  setup as terrainSetup,
} from './Terrain';
import { sprite } from './Textures';
import { clamp as toolsClamp, length as toolsLength, rand as toolsRand } from './Tools';
import type { PixiSpritesByName } from './types/Graphics';
import {
  applyMobileLayout as uiApplyMobileLayout,
  getScalingFactor as uiGetScalingFactor,
  markHudStale as uiMarkHudStale,
  minimapLayout,
  resizeHUD as uiResizeHUD,
  resizeLaserPointer as uiResizeLaserPointer,
  resizeMinimap as uiResizeMinimap,
  setupHUD as uiSetupHUD,
  setupLaserPointer as uiSetupLaserPointer,
  setupMinimap as uiSetupMinimap,
  setupMinimapScaling as uiSetupMinimapScaling,
  takeHudEnergyStale as uiTakeHudEnergyStale,
  takeHudHealthStale as uiTakeHudHealthStale,
} from './UI';
import Vector from './Vector';

/** The canvas as the renderer sized it: its size in canvas pixels, its offset from the window's left edge, and the world scale. */
export interface Viewport {
  /** The canvas size in canvas pixels. */
  width: number;
  height: number;
  halfWidth: number;
  halfHeight: number;
  /** Horizontal gap between window and canvas left edges (viewport px). 0
   *  normally; positive when squaredScene centers a width-capped canvas.
   *  Add to canvas-space x to get viewport x. */
  offsetX: number;
  /** Canvas pixels per world unit. */
  scale: number;
  /** Canvas pixels per CSS pixel: the screen's ratio with HiDPI on, 1 otherwise. */
  resolution: number;
}
/**
 * The viewport this module sizes in resizeRenderer and applyScale. Writes go
 * through this binding; reads, this module's included, through the read-only
 * view below.
 */
const viewportState: Viewport = {
  width: 0,
  height: 0,
  halfWidth: 0,
  halfHeight: 0,
  offsetX: 0,
  scale: 1,
  resolution: 1,
};
export const viewport: Readonly<Viewport> = viewportState;

interface CameraState {
  /** Current camera position in world coordinates */
  position: Vector;

  /** Target center point for the camera */
  center: Vector;

  /** Current screen shake intensity */
  shake: number;
}

let renderer: Renderer | undefined;

/** The renderer, which exists once setup has created it; everything that draws runs after that. */
function requireRenderer(): Renderer {
  if (renderer === undefined) {
    throw new Error('Graphics.setup has not created the renderer');
  }
  return renderer;
}

/** Map outline graphics for hitshapes theme (separate from polygon mask) */
let outlines: PixiGraphics | null = null;

/** Map land fill graphics for hitshapes theme (subtle filled polygons) */
let mapFill: PixiGraphics | null = null;

/** Procedural sea grid for hitshapes theme. Drawn in world space with
 *  Pixi 8's `pixelLine` stroke so the lines stay exactly 1 screen pixel
 *  wide regardless of zoom — the texture-tiling approach can't deliver
 *  uniform line thickness because tile boundaries land at fractional
 *  screen positions when viewport.scale isn't an integer multiple. */
let seaGrid: PixiGraphics | null = null;

/** CTF spawn-zone boundaries in world coordinates. IMPORTANT: these mirror
 *  ab-server's CTF respawn logic — the server never transmits spawn zones,
 *  so (like the De extension we harvested the geometry from) they are
 *  hardcoded client-side and would silently drift if a server changed its
 *  spawn rules. Blue respawns right of x=0 / below y=0; red respawns left
 *  of x=-1024 / below y=-512. */
const CTF_SPAWN_BLUE_X = 0;
const CTF_SPAWN_RED_X = -1024;
const CTF_SPAWN_BLUE_Y = 0;
const CTF_SPAWN_RED_Y = -512;

/** CTF spawn-boundary lines (harvested from the De extension): blue vertical
 *  + half-width horizontal, red vertical + half-width horizontal (shape
 *  reads as  -| |-  across the map). Drawn with pixelLine strokes like the
 *  hitshapes grid so they stay 1 screen pixel at any zoom (no aliasing).
 *  Works in all themes; only shown in CTF when the setting is on. */
let ctfSpawnLines: PixiGraphics | null = null;
let ctfSpawnLinesEnabled = false;

/** Same lines on the minimap (De's respawnLinesMinimap). Built lazily in the
 *  update loop, rebuilt only when minimap size/position changes. */
let minimapSpawnLines: PixiGraphics | null = null;
// Last minimap anchor/size the lines were rebuilt for. NaN forces a rebuild on
// the first visible frame; compared field-by-field so the hot path allocates
// no per-frame key string.
let minimapSpawnLinesRight = NaN;
let minimapSpawnLinesBottom = NaN;
let minimapSpawnLinesSize = NaN;

export function setCtfSpawnLinesEnabled(enabled: boolean) {
  ctfSpawnLinesEnabled = enabled;
}

const cameraState: CameraState = {
  position: Vector.zero(),
  center: Vector.zero(),
  shake: 0,
};

/** HUD and minimap sprites: the bars are made here, the minimap in UI.setupMinimap. */
export const gui: Partial<PixiSpritesByName> = {};

/**
 * The world-space overlays, built by initMapTextures and empty until then, so
 * the per-frame loop is a no-op before setup: Graphics that own their
 * transform, the polygon mask, and the outlines, fill, grid and spawn lines
 * when present. The terrain itself is the two quads of Terrain.ts, which
 * follow the camera through their uniforms.
 */
const worldOverlays: PixiGraphics[] = [];

/** The sea quad, kept to hand for the grid toggle. */
let seaQuad: Container | null = null;

/** The screen shade in the shadows container, scaled to cover the viewport. */
let shade: Sprite | null = null;

/** Initializes the full rendering pipeline. Must be called once before the game starts. */
export async function setup() {
  initGameObjScreenVars(window.innerWidth, window.innerHeight);

  // Initialize PixiJS Assets API with manifest
  const theme = settings.display?.theme ?? DISPLAY_DEFAULTS.theme;
  const basePath = theme === 'default' ? '/vanilla/game' : `/${theme}/game`;
  await Assets.init({
    basePath,
    manifest: `${basePath}/pixi-bundles.json`,
  });

  // Load all game assets with progress tracking
  try {
    await Assets.loadBundle('game-core', (progress: number) => {
      if (DEVELOPMENT) console.log(`Loading assets: ${Math.round(progress * 100)}%`);
    });
  } catch (error) {
    console.error('Asset loading failed:', error);
    throw error;
  }

  // Continue setup after assets loaded
  await setupPixiRenderer();
  initContainerScales();
  initPixiTextures();

  // Disable visual effect layers for hitshapes theme (improves visibility of hitshapes)
  if (theme === 'hitshapes') {
    layers.shadows.visible = false;
    layers.smoke.visible = false;
    layers.glows.visible = false;
  }

  doodadsSetup(); // Must be before initMapTextures, which runs the first doodad update
  await initMapTextures();
  uiSetupMinimap();
  uiSetupMinimapScaling();
  uiSetupHUD();
  uiSetupLaserPointer();
}

/**
 * The resolution the canvas renders at: the screen's pixel ratio with the
 * HiDPI setting on, 1 otherwise. The original had the same switch at a fixed
 * 2; past 3 the pixels are beyond the eye and the fill is not. Read once at
 * boot, since the renderer, the HUD textures and the bitmap fonts are all
 * built with it, so a change takes effect on the next load.
 */
export function renderResolution(): number {
  const hidpi = settings.display?.hidpi ?? DISPLAY_DEFAULTS.hidpi;
  return hidpi ? Math.min(3, Math.max(1, window.devicePixelRatio || 1)) : 1;
}

/** Creates the WebGPU or WebGL renderer and appends its canvas to the document.
 *  The canvas is intentionally larger than the viewport by `OVERDRAW`
 *  on each axis, positioned with negative CSS offsets so it overhangs the
 *  visible area on all sides. The browser's `body { overflow: hidden }` clips
 *  the overhang for free, giving the render-past-screen-edge effect without
 *  an off-screen RenderTexture or composite pass. */
async function setupPixiRenderer() {
  const rendererPref = settings.display?.rendererPreference;

  // The original forced highp fragment shaders. Pixi 8 defaults its WebGL ones
  // to mediump, which desktop GPUs run as highp anyway and WebGPU never uses,
  // but mobile WebGL honours: the terrain shaders sample the masks with
  // coordinates that span the whole map, and at 16 bits those step by texels.
  // Pixi still drops to mediump where a GPU has no highp fragments.
  GlProgram.defaultOptions.preferredFragmentPrecision = 'highp';

  const pixiSettings: Partial<AutoDetectOptions> = {
    width: viewport.width + OVERDRAW,
    height: viewport.height + OVERDRAW,
    // HiDPI renders at the screen's pixel ratio (see renderResolution);
    // autoDensity keeps the canvas's CSS size in CSS pixels, so the sizes and
    // offsets here, and the input, never see the difference.
    resolution: renderResolution(),
    autoDensity: true,
    // Opaque black. The page behind the canvas is black and the sea or the
    // grid always covers the viewport, so nothing ever showed through, and an
    // opaque canvas composites without a full-screen blend. Render textures
    // are unaffected: Pixi applies the background to the screen only.
    background: 0x000000,
    backgroundAlpha: 1,
    // Disable PixiJS GC — it destroys GPU buffers after 60s of inactivity,
    // which causes WebGPU "buffer used while destroyed" errors when the tab
    // is backgrounded. All textures are loaded upfront and managed manually.
    gcActive: false,
    // Keep Tab away from Pixi's accessibility layer. Tab is the scoreboard key,
    // and Pixi 8.22 restored its default of switching the layer on at that key
    // (a regression had left the listener unregistered since 8.14). Once on,
    // the layer walks the whole scene graph after every render until the mouse
    // moves, and the game marks nothing as accessible for it to expose.
    accessibilityOptions: { activateOnTab: false },
    // WebGPU first unless the user forced WebGL; Pixi falls back to WebGL on
    // its own where WebGPU is unsupported. The array form keeps Pixi's 2D
    // canvas renderer out of the fallback order: it draws this scene with the
    // land black, where the original showed its "WebGL required" message.
    // Strict comparison doubles as the legacy guard: the retired 'auto' value
    // (which effectively meant WebGL, Pixi's default preference) reads as
    // webgpu-first.
    preference: rendererPref === 'webgl' ? ['webgl'] : ['webgpu', 'webgl'],
  };
  let created: Renderer;
  try {
    created = await autoDetectRenderer(pixiSettings);
  } catch (error) {
    // Neither API is available. The message explains, and the throw stops the
    // boot before the game loop starts; index.ts leaves the message up.
    uiState.bigMessage = 'webgl';
    throw error;
  }
  renderer = created;
  viewportState.resolution = created.resolution;

  // Fully disable Pixi's interaction system. The game uses zero Pixi pointer
  // events — click-to-shoot, autopilot minimap clicks, and all Lit UI run on
  // DOM listeners (window mousedown/click). Left enabled, Pixi runs a
  // point-in-polygon `containsPoint` against the map's stencil mask geometry
  // (thousands of vertices) on every pointer move — profiled at ~31-44% of
  // frame time. destroy() detaches the DOM listeners and stops the events
  // ticker, so pointer events never reach the EventBoundary at all.
  created.events.destroy();

  // Overhang the viewport by overdraw/2 on each side. body has overflow:hidden
  // so the overhang gets clipped for free. z-index: -1 keeps the canvas behind
  // all UI elements (Lit components, HUD overlays) — the UI's z ladder uses positive
  // z-indexes only, so nothing else fights for the negative layer.
  created.canvas.style.position = 'absolute';
  positionCanvasX();
  created.canvas.style.top = `${-OVERDRAW / 2}px`;
  created.canvas.style.zIndex = '-1';
  // The game owns every touch on the canvas. body says the same for the page; the canvas, the one light-DOM
  // element the UI does not style, carries its own copy here with its other inline styles.
  created.canvas.style.touchAction = 'none';
  document.body.appendChild(created.canvas);

  // A restored GL context loses render-texture contents, and the HUD arcs are
  // only redrawn when their inputs change, so flag them for a redraw here.
  created.canvas.addEventListener('webglcontextrestored', uiMarkHudStale);

  // iOS shows a text-selection magnifier/callout on a long-press over the canvas
  // even with `user-select: none` — a system gesture CSS can't suppress.
  // Preventing the canvas `touchstart` default kills it. Mobile-only, and only
  // while PLAYING: in the lobby the canvas background must stay clickable — the
  // game-selector (and other popovers) close via a document-level outside-click,
  // which a cancelled synthesized click would break, and `touchCloseAll` no-ops
  // outside PLAYING so there's no fallback there. In-game there are no canvas
  // touches to preserve (joystick/fire/special are separate DOM, autopilot is
  // desktop Alt+click, mouse-fire is desktop), and panel-close runs off the
  // window touchstart, which still fires (preventDefault stops the default
  // action, not propagation).
  if (device.mobile) {
    created.canvas.addEventListener(
      'touchstart',
      (event) => {
        if (session.state === GAME_STATE_CODES.PLAYING) event.preventDefault();
      },
      { passive: false },
    );
  }
}

/**
 * Applies the canvas's horizontal placement: `offsetX` centers a
 * width-capped canvas when squaredScene is on (0 otherwise), less the
 * `overdraw/2` overhang.
 *
 * The clip is what keeps the letterbox clean. The canvas overhangs the viewport
 * by `overdraw/2` on every side, and normally that overhang sits outside the
 * window where `body { overflow: hidden }` clips it for free. Once squaredScene
 * insets the canvas, the left and right margins land *inside* the window — and
 * they are live render area: terrain is sized to exactly `width`, but the
 * object layers (projectiles, aircraft, doodads, nameplates) draw across the
 * whole canvas, so they spill onto the black bands. Trimming the two margins
 * back to the viewport edge reproduces the off-window clip we get for free
 * everywhere else. Top/bottom still hang off-window, so they need no clip.
 */
function positionCanvasX() {
  const margin = OVERDRAW / 2;
  const canvas = requireRenderer().canvas;
  canvas.style.left = `${viewport.offsetX - margin}px`;
  canvas.style.clipPath = viewport.offsetX > 0 ? `inset(0 ${margin}px)` : '';
}

/** Sets up the shadows blend, the shade overlay and the HUD render textures. Layers.ts parents the layers. */
function initPixiTextures() {
  // Configure shadows container for direct rendering (no RenderTexture)
  layers.shadows.blendMode = 'multiply';
  layers.shadows.alpha = 0.5; // Darker shadows

  // Create screen shade sprite (container is scaled by viewport.scale * shadowScaling)
  const screenShade = sprite('screenshade');
  shade = screenShade;
  updateShadeScale();
  screenShade.alpha = 0.4;
  screenShade.anchor.set(0.5, 0.5);
  layers.shadows.addChild(screenShade);

  // Create HUD energy bar texture and sprite
  gui.hudTextureEnergy = RenderTexture.create({
    width: 80,
    height: 348,
    resolution: viewport.resolution,
  });
  gui.hudSpriteEnergy = new Sprite(gui.hudTextureEnergy);
  gui.hudSpriteEnergy.pivot.set(-250, 174);

  // Create HUD health bar texture and sprite
  gui.hudTextureHealth = RenderTexture.create({
    width: 80,
    height: 348,
    resolution: viewport.resolution,
  });
  gui.hudSpriteHealth = new Sprite(gui.hudTextureHealth);
  gui.hudSpriteHealth.pivot.set(330, 174);

  // Add HUD sprites to game container
  layers.game.addChild(gui.hudSpriteEnergy);
  layers.game.addChild(gui.hudSpriteHealth);

  // The canvas is oversized by `OVERDRAW` and positioned with negative
  // CSS offsets (see setupPixiRenderer). The game container is shifted by
  // `+overdraw/2` so world coordinates render into the visible viewport,
  // with sprites up to `overdraw/2` past the screen edge still drawn before
  // the browser clips the canvas overhang. No RT/composite needed.
  layers.game.position.set(OVERDRAW / 2, OVERDRAW / 2);
}

/** Scales the shade to cover the viewport in shadow space (its container is scaled by viewport.scale * shadowScaling). */
function updateShadeScale() {
  if (shade === null) return;
  shade.scale.set(
    viewport.width / 126 / viewport.scale / SHADOW_SCALING,
    viewport.height / 126 / viewport.scale / SHADOW_SCALING,
  );
}

/**
 * Resizes the renderer and all dependent textures, sprites, and UI elements.
 *
 * @param width - New width in pixels
 * @param height - New height in pixels
 */
export function resizeRenderer(width: number, height: number) {
  // Update screen dimensions. viewport.width may come out narrower than the
  // window width (squaredScene cap) — every size below must use
  // viewport.width/height, not the raw params.
  initGameObjScreenVars(width, height);

  // Resize renderer to (viewport + overdraw). The canvas's CSS top offset
  // stays at -overdraw/2; left additionally carries offsetX so a
  // width-capped canvas re-centers on every resize (and snaps back to the
  // full window when squaredScene turns off).
  requireRenderer().resize(viewport.width + OVERDRAW, viewport.height + OVERDRAW);
  positionCanvasX();

  // The terrain quads are viewport-sized (see initMapTextures)
  terrainResize(viewport.width, viewport.height);
  uiResizeLaserPointer();

  applyScale();

  // Update network horizon if playing
  if (session.state === GAME_STATE_CODES.PLAYING) {
    networkResizeHorizon();
  }
}

/**
 * Reapplies the current scaling factor without reallocating the renderer or
 * the terrain tiles. Use when zoom changes but window dimensions don't.
 */
export function applyScaleOnly() {
  applyScale();

  // If `scaleNameplates` is enabled, font sizes depend on zoom — refresh now.
  // No-op when disabled (the multiplier short-circuits to 1, no observable change).
  if (settings.game?.scaleNameplates ?? GAME_DEFAULTS.scaleNameplates) {
    playersForEach((player) => player.refreshNameplateScale());
  }
}

/** Applies viewport.scale to everything that depends on it, then re-centres the camera on the same point. */
function applyScale() {
  initContainerScales();
  updateShadeScale();
  uiResizeMinimap();
  uiResizeHUD();
  scaleTerrain();
  scaleWorldOverlays();
  setCamera(cameraState.center.x, cameraState.center.y);
}

/** Iterates all players and refreshes nameplate font sizes. Used when the
 *  `scaleNameplates` toggle changes (zoom-driven refreshes go through `applyScaleOnly`). */
export function refreshAllNameplateScales() {
  playersForEach((player) => player.refreshNameplateScale());
}

/** StarMash's `ui.useSquaredScene` cap: game viewport width ≤ 1.15 × height. */
const SQUARED_SCENE_MAX_ASPECT = 1.15;

function initGameObjScreenVars(screenInnerWidth: number, screenInnerHeight: number) {
  // "Squared game area" (StarMash ui.useSquaredScene port): cap the game
  // viewport's width at 1.15× its height; the canvas is then centered via
  // offsetX (applied to canvas CSS in setup/resize) while the DOM UI
  // keeps the full window. Desktop-only — touch layouts place controls at
  // the window edges and assume a full-width playfield.
  const windowWidth = screenInnerWidth;
  if ((settings.game?.squaredScene ?? GAME_DEFAULTS.squaredScene) && !device.mobile) {
    screenInnerWidth = Math.min(screenInnerWidth, Math.round(SQUARED_SCENE_MAX_ASPECT * screenInnerHeight));
  }
  viewportState.offsetX = Math.round((windowWidth - screenInnerWidth) / 2);
  viewportState.width = screenInnerWidth;
  viewportState.height = screenInnerHeight;
  viewportState.halfWidth = screenInnerWidth / 2;
  viewportState.halfHeight = screenInnerHeight / 2;
}

/**
 * Sets container scales from current screen dimensions.
 * Combined width+height scaling means wider screens zoom in more, balancing horizontal FOV advantage.
 */
function initContainerScales() {
  // Original scaling formula: wider screens zoom in more
  viewportState.scale = (viewport.width + viewport.height) / uiGetScalingFactor();
  layers.groundobjects.scale.set(viewport.scale);
  layers.objects.scale.set(viewport.scale);
  // Shadow sprites were scaled for shadow-space (divided by shadowScaling), so multiply back
  layers.shadows.scale.set(viewport.scale * SHADOW_SCALING);
  layers.doodads.scale.set(viewport.scale);
  layers.bubbles.scale.set(1 / viewport.scale);
  uiApplyMobileLayout();
}

/** Builds the hitshapes-theme sea grid: a Pixi Graphics drawn in world space
 *  with `pixelLine` strokes (Pixi 8) so each grid line is exactly 1 screen
 *  pixel wide regardless of zoom — solves the tile-boundary aliasing that
 *  texture-tiling can't fix when viewport.scale produces fractional tile sizes.
 *  Also paints a black background covering the visible area so we can drop
 *  the sea quad entirely for this theme. */
function buildHitshapesSeaGrid(): PixiGraphics {
  const spacing = 1024; // matches the original map_sea tile pitch in world units
  // World space is centered on the origin: the map spans ±MAP_CENTER_X by
  // ±MAP_CENTER_Y (same frame as the edge borders). Extend past those bounds
  // by a padding margin so the grid still covers the screen when the camera
  // flies past the edge (extendMapEdges setting).
  const padding = 10000;
  const minX = -MAP_CENTER_X - padding;
  const maxX = MAP_CENTER_X + padding;
  const minY = -MAP_CENTER_Y - padding;
  const maxY = MAP_CENTER_Y + padding;

  const g = new PixiGraphics();
  // Black background — replaces the sea quad for this theme.
  g.rect(minX, minY, maxX - minX, maxY - minY).fill({ color: 0x000000, alpha: 1 });
  // Vertical lines — single path, single stroke for batching.
  for (let x = Math.ceil(minX / spacing) * spacing; x <= maxX; x += spacing) {
    g.moveTo(x, minY).lineTo(x, maxY);
  }
  // Horizontal lines.
  for (let y = Math.ceil(minY / spacing) * spacing; y <= maxY; y += spacing) {
    g.moveTo(minX, y).lineTo(maxX, y);
  }
  g.stroke({ width: 1, color: 0xffffff, alpha: 0.4, pixelLine: true });
  return g;
}

/** Builds the CTF spawn-boundary lines in world space (origin-centered, same
 *  frame as the sea grid). Geometry harvested from the De extension: each
 *  team's respawn area is bounded by a vertical line (blue x=0, red x=-1024)
 *  plus a horizontal line covering that team's half (blue y=0 to the right,
 *  red y=-512 to the left; De's left line overhung the map edge by 1024 —
 *  clamped to the edge here). pixelLine strokes keep them crisp at any zoom. */
function buildCtfSpawnLines(): PixiGraphics {
  const g = new PixiGraphics();

  // Blue (team 1) — vertical boundary, plus horizontal spanning the right half
  g.moveTo(CTF_SPAWN_BLUE_X, -MAP_CENTER_Y).lineTo(CTF_SPAWN_BLUE_X, MAP_CENTER_Y);
  g.moveTo(CTF_SPAWN_BLUE_X, CTF_SPAWN_BLUE_Y).lineTo(MAP_CENTER_X, CTF_SPAWN_BLUE_Y);
  g.stroke({ width: 1, color: 0x0000ff, alpha: 0.65, pixelLine: true });

  // Red (team 2) — vertical boundary, plus horizontal spanning the left half
  g.moveTo(CTF_SPAWN_RED_X, -MAP_CENTER_Y).lineTo(CTF_SPAWN_RED_X, MAP_CENTER_Y);
  g.moveTo(-MAP_CENTER_X, CTF_SPAWN_RED_Y).lineTo(CTF_SPAWN_RED_X, CTF_SPAWN_RED_Y);
  g.stroke({ width: 1, color: 0xff0000, alpha: 0.65, pixelLine: true });

  g.visible = false;
  return g;
}

/** Lazily creates / rebuilds the minimap spawn lines (De's respawnLinesMinimap)
 *  and keeps their visibility in sync. Uses the same world→minimap mapping as
 *  UI.ts updateMinimapMob; rebuilds only when the minimap's size or anchor
 *  changes (resize, minimap-scale setting). Added to the minimap background's
 *  own layer, after it — above the map art, below the ui4 viewport box. */
function updateMinimapSpawnLines(scaledSize: number) {
  const minimapBg = gui.minimap;
  if (!minimapBg || !minimapBg.parent) return;

  if (!minimapSpawnLines) {
    minimapSpawnLines = new PixiGraphics();
    minimapBg.parent.addChild(minimapSpawnLines);
  }

  minimapSpawnLines.visible = ctfSpawnLinesEnabled && session.gameType === GAME_TYPES.CTF && minimapBg.visible;
  if (!minimapSpawnLines.visible) return;

  const right = viewport.width - minimapLayout.paddingX;
  const bottom = viewport.height - minimapLayout.paddingY;
  // Skip the rebuild unless the minimap anchor or size actually changed.
  if (right === minimapSpawnLinesRight && bottom === minimapSpawnLinesBottom && scaledSize === minimapSpawnLinesSize) {
    return;
  }
  minimapSpawnLinesRight = right;
  minimapSpawnLinesBottom = bottom;
  minimapSpawnLinesSize = scaledSize;

  const left = right - scaledSize;
  const top = bottom - scaledSize / 2;
  const toMiniX = (wx: number) => right - scaledSize * ((MAP_CENTER_X - wx) / MAP_WIDTH);
  const toMiniY = (wy: number) => bottom - (scaledSize / 2) * ((MAP_CENTER_Y - wy) / MAP_HEIGHT);

  const g = minimapSpawnLines;
  g.clear();
  const blueX = toMiniX(CTF_SPAWN_BLUE_X);
  const blueY = toMiniY(CTF_SPAWN_BLUE_Y);
  g.moveTo(blueX, top).lineTo(blueX, bottom);
  g.moveTo(blueX, blueY).lineTo(right, blueY);
  g.stroke({ width: 1, color: 0x0000ff, alpha: 0.35, pixelLine: true });

  const redX = toMiniX(CTF_SPAWN_RED_X);
  const redY = toMiniY(CTF_SPAWN_RED_Y);
  g.moveTo(redX, top).lineTo(redX, bottom);
  g.moveTo(left, redY).lineTo(redX, redY);
  g.stroke({ width: 1, color: 0xff0000, alpha: 0.35, pixelLine: true });
}

/** Creates terrain tiling textures, masks, and loads map polygon geometry from JSON. */
async function initMapTextures() {
  const theme = settings.display?.theme ?? DISPLAY_DEFAULTS.theme;

  // The terrain quads are viewport-sized, not canvas-sized. The `game` container
  // is offset by overdraw/2, so width × height covers exactly the visible
  // viewport; the overdraw margin is never seen (it hangs off-window normally,
  // and positionCanvasX clips it once squaredScene insets the canvas), so
  // drawing into it is pure wasted fill. resizeRenderer keeps them at these
  // dimensions.
  const terrain = terrainSetup(requireRenderer());
  terrainResize(viewport.width, viewport.height);
  seaQuad = terrain.sea;

  // Sea in its own container (NOT masked by polygons)
  layers.sea.addChild(terrain.sea);

  // Hitshapes theme: hide the texture-tiled sea (which contains the original
  // shimmer-prone grid) and use a pixelLine Graphics grid instead. The grid
  // is positioned/scaled like `outlines` in the render loop.
  if (theme === 'hitshapes') {
    terrain.sea.visible = false;
    const grid = buildHitshapesSeaGrid();
    grid.visible = settings.display?.grid ?? DISPLAY_DEFAULTS.grid;
    layers.sea.addChild(grid);
    worldOverlays.push(grid);
    seaGrid = grid;
  }

  // CTF spawn-boundary lines — all themes. Inserted as a direct child of the
  // static game container (right above groundobjects: over terrain, below
  // shadows/planes) and self-transformed like seaGrid/outlines. Putting them
  // INSIDE the world-scaled groundobjects container instead made pixelLine
  // strokes render with a soft anti-aliased glow — the exact shimmer the
  // pixelLine grid exists to avoid — so they must own their transform.
  const spawnLines = buildCtfSpawnLines();
  layers.game.addChildAt(spawnLines, layers.game.getChildIndex(layers.groundobjects) + 1);
  worldOverlays.push(spawnLines);
  ctfSpawnLines = spawnLines;

  // The land in the map container (WILL be masked by polygons)
  layers.map.addChild(terrain.land, layers.doodads);

  // Hide the land for hitshapes theme (doodads/mountains remain visible)
  if (theme === 'hitshapes') {
    terrain.land.visible = false;
  }

  // Polygons: child + mask of the map container, and the hitshapes overlays
  await createMapFromJson();
  scaleWorldOverlays();
  scaleTerrain();

  // 4 rectangle sprites just outside map bounds — fill the void when the camera
  // extends past edges (extendMapEdges setting). Visibility toggled at runtime.
  createMapEdgeBorders();

  // Initialize doodad positions for the current view
  doodadsUpdate();
}

/** Thin frame at the map edge — past this, the unmasked sea (default theme) or
 *  grid (hitshapes theme with grid on) keeps rendering, so the void doesn't
 *  feel like a hard box. Layer order: sea → map → groundobjects (borders).
 *  White matches SWAM and reads cleanly in hitshapes; in default theme it sits
 *  over the sea so it's visible but not jarring. */
const EDGE_BORDER_THICKNESS = 100;
const EDGE_BORDER_COLOR = 0xffffff;
const EDGE_BORDER_ALPHA = 0.5;

let mapEdgeBorders: PixiGraphics[] = [];

function createMapEdgeBorders() {
  const layer = layers.groundobjects;

  for (const b of mapEdgeBorders) b.destroy();
  mapEdgeBorders = [];

  const total = MAP_HEIGHT + EDGE_BORDER_THICKNESS * 2;
  const wide = MAP_WIDTH + EDGE_BORDER_THICKNESS * 2;

  // West / East — full-height vertical strips
  const west = new PixiGraphics()
    .rect(-MAP_CENTER_X - EDGE_BORDER_THICKNESS, -MAP_CENTER_Y - EDGE_BORDER_THICKNESS, EDGE_BORDER_THICKNESS, total)
    .fill({ color: EDGE_BORDER_COLOR, alpha: EDGE_BORDER_ALPHA });
  const east = new PixiGraphics()
    .rect(MAP_CENTER_X, -MAP_CENTER_Y - EDGE_BORDER_THICKNESS, EDGE_BORDER_THICKNESS, total)
    .fill({ color: EDGE_BORDER_COLOR, alpha: EDGE_BORDER_ALPHA });
  // North / South — full-width horizontal strips (overlap the corners)
  const north = new PixiGraphics()
    .rect(-MAP_CENTER_X - EDGE_BORDER_THICKNESS, -MAP_CENTER_Y - EDGE_BORDER_THICKNESS, wide, EDGE_BORDER_THICKNESS)
    .fill({ color: EDGE_BORDER_COLOR, alpha: EDGE_BORDER_ALPHA });
  const south = new PixiGraphics()
    .rect(-MAP_CENTER_X - EDGE_BORDER_THICKNESS, MAP_CENTER_Y, wide, EDGE_BORDER_THICKNESS)
    .fill({ color: EDGE_BORDER_COLOR, alpha: EDGE_BORDER_ALPHA });

  for (const b of [west, east, north, south]) {
    layer.addChild(b);
    mapEdgeBorders.push(b);
  }

  applyExtendMapEdgesVisibility();
}

/** Cached `extendMapEdges` flag — read by setCamera every frame. Synced from
 *  config whenever the setting toggles via applyExtendMapEdgesVisibility(). */
let extendMapEdgesEnabled = true;

/** Sync border sprite visibility AND the cached flag with the current setting. */
export function applyExtendMapEdgesVisibility() {
  extendMapEdgesEnabled = settings.game?.extendMapEdges ?? GAME_DEFAULTS.extendMapEdges;
  for (const b of mapEdgeBorders) {
    b.visible = extendMapEdgesEnabled;
  }
}

/** Applies the current game scale to the terrain quads. */
function scaleTerrain() {
  terrainSetScale(viewport.scale);
}

/** Applies current game scale to the world-space overlays (polygon mask, outlines, fill, grid, spawn lines). */
function scaleWorldOverlays() {
  for (const overlay of worldOverlays) {
    overlay.scale.set(viewport.scale, viewport.scale);
  }
}

/**
 * The coastline file. In development a `map` query parameter swaps it, so a
 * candidate written by tools/map/build-map.ts can be A/B'd against the shipped
 * one without moving files: a bare name reads /map.<name>.json, a path is
 * fetched as given.
 */
function mapFileUrl(): string {
  if (!DEVELOPMENT) return '/map.json';
  const requested = new URLSearchParams(window.location.search).get('map');
  if (requested === null || requested === '') return '/map.json';
  return requested.startsWith('/') ? requested : `/map.${requested}.json`;
}

/**
 * Loads and creates map polygons from JSON data.
 *
 * Fetches the map.json file and generates PIXI polygons for the game map boundaries.
 * The map data uses relative coordinates that are accumulated as polygons are drawn.
 * Inner polygons are added as holes to create complex map shapes.
 * The resulting polygon graphics are used as a mask for the map container.
 */
async function createMapFromJson() {
  const response = await fetch(mapFileUrl());
  const json: unknown = await response.json();
  const mapResponse = json as number[][][];

  // The map container's mask. Pixi 8 masks with the stencil buffer, so the
  // fill only turns each path into geometry: its colour and alpha never show.
  const polygons = new PixiGraphics();
  const isHitshapes = (settings.display?.theme ?? DISPLAY_DEFAULTS.theme) === 'hitshapes';

  // Separate graphics for hitshapes outlines and fill (can't reuse polygons — it's the mask)
  if (isHitshapes) {
    outlines = new PixiGraphics();
    mapFill = new PixiGraphics();
  }

  // Accumulated coordinate offsets for relative positioning
  let accumulatedX = 0;
  let accumulatedY = 0;

  // Each group is a shape: the first polygon is the land, filled; the rest are
  // its lakes, drawn into the path after the fill and cut from it together.
  // One cut per shape matters: Pixi's cut() looks back over the last two
  // instructions, so a second or later cut on a shape also hands the lake to
  // the previous shape's fill, and earcut bridges that outside hole into a
  // slab of land across the sea (Australia's lakes once joined Madagascar to
  // the outback). A single cut takes all the lakes as one path and breaks
  // out of that look-back on the shape they belong to.
  for (const polygonGroup of mapResponse) {
    let polyIndex = 0;

    for (const polygonData of polygonGroup) {
      const points: number[] = [];

      // Build polygon points from relative coordinates
      for (let coordIndex = 0; coordIndex < polygonData.length; coordIndex += 2) {
        const x = polygonData[coordIndex]! + accumulatedX;
        const y = polygonData[coordIndex + 1]! + accumulatedY;

        points.push(x, -y);

        accumulatedX = x;
        accumulatedY = y;
      }

      polygons.poly(points);
      if (polyIndex === 0) {
        polygons.fill(0xffffff);
      }

      // Hitshapes: draw outline stroke to separate graphics object
      if (outlines) {
        outlines.poly(points);
        outlines.stroke({ color: 0x333333, width: 2 });
      }

      // Hitshapes: the subtle land fill, its lakes cut the same way
      if (mapFill) {
        mapFill.poly(points);
        if (polyIndex === 0) {
          mapFill.fill({ color: 0xffffff, alpha: 0.05 });
        }
      }

      polyIndex++;
    }

    if (polyIndex > 1) {
      polygons.cut();
      if (mapFill) {
        mapFill.cut();
      }
    }
  }

  // A mask isn't drawn as a child (Pixi leaves it out of the build); it is
  // added so it takes the map's transform, which the stencil is placed with.
  layers.map.addChild(polygons);
  layers.map.mask = polygons;
  worldOverlays.push(polygons);

  // Fill and outlines go in the sea container: behind the map, not clipped by its mask
  if (mapFill) {
    mapFill.visible = settings.display?.mapFill ?? DISPLAY_DEFAULTS.mapFill;
    layers.sea.addChild(mapFill);
    worldOverlays.push(mapFill);
  }
  if (outlines) {
    outlines.visible = settings.display?.outlines ?? DISPLAY_DEFAULTS.outlines;
    layers.sea.addChild(outlines);
    worldOverlays.push(outlines);
  }
}

/** Sets position, scale, rotation, and alpha on a container. Scale is uniform when yScale is omitted. */
export function transform(
  container: Container,
  xPos: number,
  yPos: number,
  rot?: number | null,
  xScale?: number | null,
  yScale?: number | null,
  alpha?: number | null,
) {
  // Set position
  container.position.set(xPos, yPos);

  // Set scale (per-axis or uniform)
  if (xScale != null && yScale != null) {
    container.scale.set(xScale, yScale);
  } else if (xScale != null) {
    container.scale.set(xScale);
  }

  // Set rotation
  if (rot != null) {
    container.rotation = rot;
  }

  // Set alpha
  if (alpha != null) {
    container.alpha = alpha;
  }
}

export function update() {
  // Update shade position to follow camera (shadow-space to match container children)
  if (shade !== null) {
    shade.position.set(cameraState.center.x / SHADOW_SCALING, cameraState.center.y / SHADOW_SCALING);
  }

  // Position the world-scaled layers relative to the camera. The shadows
  // container's children are in shadow-space, but it is scaled by
  // viewport.scale * shadowScaling, so it moves with the others.
  const worldX = -cameraState.position.x * viewport.scale;
  const worldY = -cameraState.position.y * viewport.scale;
  layers.objects.position.set(worldX, worldY);
  layers.groundobjects.position.set(worldX, worldY);
  layers.doodads.position.set(worldX, worldY);
  layers.shadows.position.set(worldX, worldY);

  // Update minimap box position based on player location on map (using scaled size)
  const scaledMinimapSize = minimapLayout.size * minimapLayout.scale;
  const minimapPlayerX =
    viewport.width - minimapLayout.paddingX - scaledMinimapSize * ((MAP_CENTER_X - cameraState.center.x) / MAP_WIDTH);
  const minimapPlayerY =
    viewport.height -
    minimapLayout.paddingY -
    (scaledMinimapSize / 2) * ((MAP_CENTER_Y - cameraState.center.y) / MAP_HEIGHT);
  gui.minimap_box!.position.set(minimapPlayerX, minimapPlayerY);
  // Self-dot: same spot as box centre — pinpoints exact player location while
  // the box outlines the visible region around it.
  if (gui.minimap_self) {
    gui.minimap_self.position.set(minimapPlayerX, minimapPlayerY);
  }

  // CTF spawn lines on the minimap (visibility + lazy rebuild on size change)
  updateMinimapSpawnLines(scaledMinimapSize);

  // Update doodads for the current view
  doodadsUpdate();
}

/**
 * Centers the camera on (x, y), clamped to map bounds (±16384 x, ±8192 y), with shake applied.
 */
export function setCamera(x: number, y: number) {
  // Initialize shake offsets
  let shakeOffsetX = 0;
  let shakeOffsetY = 0;

  // Apply camera shake if active
  if (cameraState.shake > 0.5) {
    shakeOffsetX = toolsRand(-cameraState.shake, cameraState.shake);
    shakeOffsetY = toolsRand(-cameraState.shake, cameraState.shake);
    cameraState.shake *= 1 - 0.06 * clock.timeFactor;
  }

  // Calculate half-screen dimensions in game units
  const halfScreenXInGameUnits = viewport.halfWidth / viewport.scale;
  const halfScreenYInGameUnits = viewport.halfHeight / viewport.scale;

  // When `extendMapEdges` is on, drop the half-screen offset from the clamp
  // so the camera tracks the player even at map edges (viewport extends past
  // bounds; thin border sprites mark the boundary, sea/grid renders behind
  // them). When off, the viewport is held inside map bounds (legacy behavior).
  // Reads a cached boolean — keep this hot path free of nested config lookups.
  const offsetX = extendMapEdgesEnabled ? 0 : halfScreenXInGameUnits;
  const offsetY = extendMapEdgesEnabled ? 0 : halfScreenYInGameUnits;
  const clampedX = toolsClamp(x, -MAP_CENTER_X + offsetX, MAP_CENTER_X - offsetX);
  const clampedY = toolsClamp(y, -MAP_CENTER_Y + offsetY, MAP_CENTER_Y - offsetY);

  // Update camera position (top-left corner of viewport)
  cameraState.position.x = shakeOffsetX + clampedX - viewport.width / 2 / viewport.scale;
  cameraState.position.y = shakeOffsetY + clampedY - viewport.height / 2 / viewport.scale;

  // Update camera center (with shake applied)
  cameraState.center.x = shakeOffsetX + clampedX;
  cameraState.center.y = shakeOffsetY + clampedY;
}

export function getCamera(): Vector {
  return cameraState.center;
}

/** Applies camera shake at the epicenter, attenuated by distance. */
export function shakeCamera(epicenter: Vector, intensity: number) {
  // Calculate distance from epicenter to camera center
  const distanceToEpicenter = toolsLength(epicenter.x - cameraState.center.x, epicenter.y - cameraState.center.y);

  // Calculate average half-screen dimension in game units
  const avgHalfScreenInGameUnits = (viewport.halfWidth / viewport.scale + viewport.halfHeight / viewport.scale) / 2;

  // Calculate shake factor based on distance (closer = stronger, max 1.3x at epicenter)
  const shakeFactor = toolsClamp(1.3 * (1 - distanceToEpicenter / avgHalfScreenInGameUnits), 0, 1);

  // Apply shake if factor is significant enough
  if (shakeFactor >= 0.1) {
    cameraState.shake = shakeFactor * intensity;
  }
}

/** A shadow's offset from its caster in world units, scaled by the mountain factor. */
const SHADOW_OFFSET_X = 20;
const SHADOW_OFFSET_Y = 40;

const _shadowScratch = new Vector(0, 0);
/** Returns position converted to shadow-container coordinate space, with doodad-based offset. */
export function shadowCoords(position: Vector): Vector {
  const doodadFactor = doodadsMountainFactor(position);
  _shadowScratch.x = (position.x + SHADOW_OFFSET_X * doodadFactor) / SHADOW_SCALING;
  _shadowScratch.y = (position.y + SHADOW_OFFSET_Y * doodadFactor) / SHADOW_SCALING;
  return _shadowScratch;
}

/** Positions a container on the minimap based on its world coordinates. */
export function minimapMob(container: Container | null, x: number, y: number) {
  if (container != null) {
    // Convert world coordinates to minimap position (using scaled size)
    const scaledSize = minimapLayout.size * minimapLayout.scale;
    const minimapX = viewport.width - minimapLayout.paddingX - scaledSize * ((MAP_CENTER_X - x) / MAP_WIDTH);
    const minimapY = viewport.height - minimapLayout.paddingY - (scaledSize / 2) * ((MAP_CENTER_Y - y) / MAP_HEIGHT);

    container.position.set(minimapX, minimapY);
  }
}

/** The renderer in use, or 'unknown' before setup has created one. */
export function getActiveRendererType(): 'webgpu' | 'webgl' | 'unknown' {
  if (renderer === undefined) return 'unknown';
  // The preference list admits only these two, so the name is one or the other.
  return renderer.name === 'webgpu' ? 'webgpu' : 'webgl';
}

/** Sets visibility of map boundary outlines (hitshapes theme only). */
export function setOutlinesVisible(visible: boolean) {
  if (outlines) {
    outlines.visible = visible;
  }
}

/** Sets visibility of map land fill (hitshapes theme only). */
export function setMapFillVisible(visible: boolean) {
  if (mapFill) {
    mapFill.visible = visible;
  }
}

export function setGridVisible(visible: boolean) {
  // For hitshapes theme the sea texture stays hidden; the visible grid is
  // the pixelLine Graphics. Other themes keep the texture-based behavior.
  if (seaGrid) {
    seaGrid.visible = visible;
    return;
  }
  if (seaQuad) {
    seaQuad.visible = visible;
  }
}

export function toggleFullscreen() {
  // iPhone Safari does not implement the Fullscreen API at all — calling
  // requestFullscreen throws a TypeError there. iPad Safari and desktop
  // browsers do support it. Runtime check rather than UA sniffing.
  if (!('requestFullscreen' in document.documentElement)) {
    return;
  }

  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {});
  } else {
    document.documentElement.requestFullscreen().catch(() => {});

    // Deliberately NOT locking orientation to landscape (removed 2026-09-05).
    // The lock only ever ran on Android — iPhone Safari bails at the guard
    // above — where it made the device physically unrotatable, reported by
    // players as "rotating the screen doesn't work". It also predates mobile
    // chat, which is a portrait-first surface: a fullscreen Android player was
    // locked out of chat entirely with no way back. iPhone has always run
    // without the lock and nobody has reported accidental rotation, so the
    // protection it offered was not worth the trap.
  }
}

/** Returns true if the world position is within the camera viewport (plus optional margin in game units). */
export function inScreen(position: Vector, margin: number): boolean {
  // Calculate half-screen dimensions in game units
  const halfScreenWidthInGameUnits = viewport.halfWidth / viewport.scale;
  const halfScreenHeightInGameUnits = viewport.halfHeight / viewport.scale;

  // Calculate screen bounds with margin
  const minX = cameraState.center.x - halfScreenWidthInGameUnits - margin;
  const maxX = cameraState.center.x + halfScreenWidthInGameUnits + margin;
  const minY = cameraState.center.y - halfScreenHeightInGameUnits - margin;
  const maxY = cameraState.center.y + halfScreenHeightInGameUnits + margin;

  // Check if position is within bounds
  return position.x >= minX && position.x <= maxX && position.y >= minY && position.y <= maxY;
}

/** Moves the terrain and the overlays under the camera, then renders the scene. */
export function render() {
  const camX = cameraState.position.x;
  const camY = cameraState.position.y;
  const tilePositionX = -camX * viewport.scale;
  const tilePositionY = -camY * viewport.scale;

  terrainSetCamera(camX, camY);
  // The world-space overlays are self-positioned like the map
  for (const overlay of worldOverlays) {
    overlay.position.set(tilePositionX, tilePositionY);
  }
  if (ctfSpawnLines !== null) {
    // Visibility per-frame: the setting can toggle mid-game and gameType changes on server switch.
    ctfSpawnLines.visible = ctfSpawnLinesEnabled && session.gameType === GAME_TYPES.CTF;
  }

  const activeRenderer = requireRenderer();

  // Redraw a HUD arc's texture only when UI.updateHUD flagged it: the arcs are
  // sprite-masked, which goes through the alpha-mask filter path, so drawing
  // them every frame was two masked passes for an unchanged image.
  if (uiTakeHudEnergyStale()) {
    activeRenderer.render({
      container: layers.hudEnergy,
      target: gui.hudTextureEnergy,
      clear: true,
    });
  }

  if (uiTakeHudHealthStale()) {
    activeRenderer.render({
      container: layers.hudHealth,
      target: gui.hudTextureHealth,
      clear: true,
    });
  }

  // Render game container directly to screen — no post-process indirection.
  activeRenderer.render({ container: layers.game, clear: true });
}
